// Where clicking a notification takes each kind of user — shared by the bell
// dropdowns and the Notifications page so both go to the same place.
// Each resolver returns { path, state? }, or null when there's nowhere to go.

/* ---------------- keep bell + page in sync ---------------- */

const NOTIFICATIONS_CHANGED = "zenith:notifications-changed";

/** Tell every open notification view (bell, page) to refetch. */
export const announceNotificationsChanged = () =>
  window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED));

/** Subscribe to the above; returns an unsubscribe function. */
export const onNotificationsChanged = (callback) => {
  window.addEventListener(NOTIFICATIONS_CHANGED, callback);
  return () => window.removeEventListener(NOTIFICATIONS_CHANGED, callback);
};

const contentOf = (n) => `${n?.title || ""} ${n?.message || ""}`.toLowerCase();

/* ---------------- admin ---------------- */

export const getAdminNotificationRoute = (notification) => {
  switch (notification?.category || "") {
    case "team_leave_application":
      return { path: "/team/leave" };
    case "leave_application":
      return { path: "/leave-management" };
    case "loan":
      return { path: "/loan-management" };
    case "payslip":
      return { path: "/payslips" };
    case "employee":
    case "profile":
    case "document":
    case "document_upload":
    case "birthday":
      return { path: "/employees" };
    case "regularization_request":
      return { path: "/attendance/regularization" };
    // Sensitive profile edits awaiting approval. Must come before the
    // content-keyword fallback, which would match "profile" and drop the admin
    // on the plain employee list instead.
    case "profile_change_requested":
    case "profile_change_approved":
    case "profile_change_rejected":
      return { path: "/employees", state: { tab: "change-requests" } };
    case "ot":
    case "overtime":
    case "ot_log":
      return { path: "/attendance/shifts", state: { tab: "overtime" } };
    case "appraisal":
      return { path: "/performance/my-appraisals" };
    case "exit_interview":
      return { path: "/performance/my-appraisals?tab=exit-interviews" };
    default:
      break;
  }

  // Fallback: keywords in the title / message.
  const content = contentOf(notification);
  if (
    content.includes("ot log") ||
    content.includes("overtime") ||
    (content.includes("ot") &&
      ["logged", "submitted", "approved", "rejected"].some((w) => content.includes(w)))
  ) {
    return { path: "/attendance/shifts", state: { tab: "overtime" } };
  }
  if (["leave", "vacation", "sick"].some((w) => content.includes(w))) {
    return { path: "/leave-management" };
  }
  if (content.includes("loan") || content.includes("advance")) {
    return { path: "/loan-management" };
  }
  if (["payslip", "salary", "payroll"].some((w) => content.includes(w))) {
    return { path: "/payslips" };
  }
  if (["employee", "staff", "profile", "birthday"].some((w) => content.includes(w))) {
    return { path: "/employees" };
  }
  if (content.includes("appraisal")) {
    return { path: "/performance/my-appraisals" };
  }
  return { path: "/dashboard" };
};

/* ---------------- an employee login that manages people ---------------- */

// Sent to their login as a manager, not to their employee record.
const getEmployeeTeamNotificationRoute = (notification) => {
  switch (notification?.category || "") {
    case "team_leave_application":
    case "leave_application":
      return { path: "/employee/team/leave" };
    case "appraisal":
      return { path: "/employee/team/reviews" };
    case "exit_interview":
      return { path: "/employee/team/reviews?tab=exit-interviews" };
    default:
      return { path: "/employee/dashboard" };
  }
};

const isToEmployeeRecord = (notification, employeeId) =>
  []
    .concat(notification?.recipient_id || [])
    .some((id) => String(id) === String(employeeId));

/** The employee portal's resolver: their own items, or their team's. */
export const getEmployeePortalNotificationRoute = (employeeId) => (notification) =>
  !employeeId || isToEmployeeRecord(notification, employeeId)
    ? getEmployeeNotificationRoute(notification)
    : getEmployeeTeamNotificationRoute(notification);

/* ---------------- admin who is also an employee ---------------- */

// One feed, two identities: a notification sent to their employee record opens
// in their own workspace, one sent to them as an admin in the admin area.
export const getLinkedAdminNotificationRoute = (employeeId) => (notification) =>
  isToEmployeeRecord(notification, employeeId)
    ? getEmployeeNotificationRoute(notification)
    : getAdminNotificationRoute(notification);

/* ---------------- employee ---------------- */

export const getEmployeeNotificationRoute = (notification) => {
  switch (notification?.category || "") {
    case "leave":
      return { path: "/employee/leave" };
    case "late_arrival":
    case "regularization_approved":
      return { path: "/employee/attendance" };
    case "loan":
      return { path: "/employee/finances" };
    case "payslip":
      return { path: "/employee/payslips" };
    // The request list lives on the profile page, so an approval or rejection
    // lands the employee where they can see the outcome.
    case "profile":
    case "profile_change_approved":
    case "profile_change_rejected":
      return { path: "/employee/profile" };
    case "document":
      return { path: "/employee/documents" };
    case "ot_approved":
    case "ot_rejected":
      return { path: "/employee/attendance", state: { tab: "overtime" } };
    case "appraisal":
    case "review_reminder":
    case "review_cancelled":
      return { path: "/employee/reviews" };
    case "pip_assigned":
    case "pip_completed":
      return { path: "/employee/reviews?tab=pips" };
    case "exit_interview":
    case "exit_interview_assigned":
      return { path: "/employee/reviews?tab=exit-interviews" };
    case "goal_reminder":
    case "goal_assigned":
      return { path: "/employee/reviews?tab=goals" };
    case "company_document_acknowledgement":
      return { path: "/employee/company-documents?tab=acknowledge" };
    default:
      break;
  }

  // Fallback: keywords in the title / message.
  const content = contentOf(notification);
  if (content.includes("leave")) return { path: "/employee/leave" };
  if (content.includes("loan") || content.includes("advance")) return { path: "/employee/finances" };
  if (content.includes("payslip") || content.includes("salary")) return { path: "/employee/payslips" };
  if (content.includes("profile")) return { path: "/employee/profile" };
  if (content.includes("document")) return { path: "/employee/documents" };
  if (content.includes("ot") || content.includes("overtime")) {
    return { path: "/employee/attendance", state: { tab: "overtime" } };
  }
  if (content.includes("appraisal")) return { path: "/employee/reviews" };
  return { path: "/employee/dashboard" };
};

/* ---------------- super admin ---------------- */

export const getSuperAdminNotificationRoute = (notification) =>
  notification?.company_id
    ? {
        path: "/super-admin/companies/details",
        state: { companyId: notification.company_id },
      }
    : null;
